export type WorkflowCategory = 'delivery' | 'diagnosis' | 'setup' | 'planning';
export type WorkflowStepKind = 'skill' | 'tool' | 'external-tool' | 'checkpoint';
export type WorkflowStatus = 'ready' | 'blocked' | 'completed' | 'checkpoint';
export type WorkflowOperation = 'create_file' | 'edit_file' | 'multi_edit' | 'generate_code' | 'review' | 'validate' | 'measure';

export interface WorkflowStep {
  id: string;
  title: string;
  goal: string;
  kind: WorkflowStepKind;
  target?: string;
  args?: Record<string, unknown>;
  note?: string;
  dependsOn?: string[];
  requiredInputs?: string[];
  requiredStandards?: string[];
  fallbackStepIds?: string[];
}

export interface WorkflowDefinition {
  id: string;
  title: string;
  subagentRole: string;
  category: WorkflowCategory;
  description: string;
  entrySignals: string[];
  frameworks: string[];
  requiredInputs: string[];
  relatedSkills: string[];
  relatedStandards: string[];
  steps: WorkflowStep[];
  completionCriteria: string[];
  codegenPrerequisites?: {
    completedStepIds: string[];
    requiredStandards?: string[];
    requiredInputs?: string[];
    note: string;
  };
}

export interface WorkflowCallSuggestion {
  kind: WorkflowStepKind;
  target?: string;
  args?: Record<string, unknown>;
  why: string;
}

export interface WorkflowRecommendation {
  workflow: {
    id: string;
    title: string;
    subagentRole: string;
    description: string;
    category: WorkflowCategory;
  };
  score: number;
  confidence: 'high' | 'medium' | 'low';
  matchedSignals: string[];
  suggestedCalls: WorkflowCallSuggestion[];
}

export interface WorkflowAdvanceArgs {
  workflowId: string;
  task?: string;
  framework?: string;
  currentFile?: string;
  completedSteps?: string[];
  blockedSteps?: string[];
  availableInputs?: string[];
  loadedStandards?: string[];
}

export interface WorkflowAdvanceResult {
  workflow: {
    id: string;
    title: string;
    subagentRole: string;
    description: string;
  };
  detectedFramework: string;
  status: WorkflowStatus;
  progress: {
    completedCount: number;
    totalCount: number;
    completedSteps: string[];
    remainingSteps: string[];
  };
  nextStep?: WorkflowStep;
  nextCall?: WorkflowCallSuggestion;
  blockedBy?: {
    stepId: string;
    missingSteps: string[];
    missingInputs: string[];
    missingStandards: string[];
  };
  fallbackCalls?: WorkflowCallSuggestion[];
  guidance: string;
}

export interface WorkflowValidationArgs {
  workflowId: string;
  operation: WorkflowOperation;
  task?: string;
  framework?: string;
  currentFile?: string;
  completedSteps?: string[];
  availableInputs?: string[];
  loadedStandards?: string[];
}

export interface WorkflowValidationResult {
  workflow: {
    id: string;
    title: string;
    operation: WorkflowOperation;
  };
  canProceed: boolean;
  detectedFramework: string;
  missingSteps: string[];
  missingInputs: string[];
  missingStandards: string[];
  suggestedCalls: WorkflowCallSuggestion[];
  reason: string;
}

const WORKFLOW_DEFINITIONS: WorkflowDefinition[] = [
  {
    id: 'sketch_restoration',
    title: '设计稿还原工作流',
    subagentRole: 'Design Restoration Specialist',
    category: 'delivery',
    description: '按 Codex 子代理的还原专员思路处理 Sketch / 设计稿任务，先量后写，再校验。',
    entrySignals: ['设计稿', 'sketch', '还原', '测量', 'compact', 'spacing', '结构漂移', '图标不对', '像素级', '视觉不一致'],
    frameworks: ['flutter', 'vue3', 'wechat-miniprogram', 'generic'],
    requiredInputs: ['目标页面或文件路径', 'Sketch 选区或测量结果', '目标框架信息'],
    relatedSkills: ['get_standards', 'sketch_measure', 'validate_restoration', 'query_cases'],
    relatedStandards: ['design-restoration', 'sketch-mcp', 'sketch-pitfalls', 'syntax-mapping'],
    steps: [
      {
        id: 'load-restoration-standards',
        title: '加载设计还原规范',
        goal: '先拿到页面结构合同、测量语义和常见陷阱，避免只靠截图猜布局。',
        kind: 'skill',
        target: 'get_standards',
        args: { ids: ['design-restoration', 'sketch-mcp', 'sketch-pitfalls'], mode: 'key-rules' },
      },
      {
        id: 'measure-compact',
        title: '先做 compact 测量',
        goal: '优先得到 restorationContract、blockOrder 和关键骨架信息。',
        kind: 'skill',
        target: 'sketch_measure',
        args: { cmd: 'compact' },
        dependsOn: ['load-restoration-standards'],
      },
      {
        id: 'run-sketch-loader',
        title: '执行 Sketch 轻量加载器',
        goal: '将脚本交给 Sketch 侧执行，拿到真实测量结果。',
        kind: 'external-tool',
        target: 'mcp_sketch_run_code',
        note: '将 sketch_measure 返回的 loaderScript 作为脚本传入。',
        dependsOn: ['measure-compact'],
        requiredInputs: ['loaderScript'],
      },
      {
        id: 'validate-output',
        title: '对照合同做还原校验',
        goal: '检查页面骨架、资源复用、路由接线和 UI review 是否达标。',
        kind: 'skill',
        target: 'validate_restoration',
        dependsOn: ['run-sketch-loader'],
        requiredInputs: ['measure-result', 'codeFilePath'],
        fallbackStepIds: ['fallback-cases'],
      },
      {
        id: 'fallback-cases',
        title: '异常时查询案例',
        goal: '图标、尺寸、资产复用或 preserve SVG 出问题时按案例回收。',
        kind: 'skill',
        target: 'query_cases',
        args: { action: 'query', framework: 'flutter' },
        dependsOn: ['validate-output'],
      },
    ],
    completionCriteria: ['结构与 restorationContract 一致', '关键交互不是静态壳', '资产来源明确且未错用 SVG/位图'],
    codegenPrerequisites: {
      completedStepIds: ['load-restoration-standards', 'measure-compact', 'run-sketch-loader'],
      requiredStandards: ['design-restoration', 'sketch-mcp'],
      requiredInputs: ['measure-result'],
      note: '设计稿还原在进入代码生成前，必须先完成规范加载与 compact 测量，并拿到真实测量结果。',
    },
  },
  {
    id: 'problem_diagnosis',
    title: '问题诊断工作流',
    subagentRole: 'Root Cause Investigator',
    category: 'diagnosis',
    description: '按 Codex 子代理的排障专员思路先定位根因，再决定查案例、补规范还是回到实现层。',
    entrySignals: ['bug', 'error', 'not working', 'broken', '回归', '问题', '异常', '样式不对', '布局不对', '颜色不对', 'regression'],
    frameworks: ['flutter', 'vue3', 'react', 'generic'],
    requiredInputs: ['问题描述或错误信息', '相关文件/路由/组件范围', '可选代码片段'],
    relatedSkills: ['troubleshoot', 'query_cases', 'get_standards'],
    relatedStandards: ['problem-diagnosis', 'design-restoration', 'sketch-pitfalls'],
    steps: [
      {
        id: 'semantic-diagnosis',
        title: '先走语义诊断入口',
        goal: '先识别框架和问题类型，不直接套表面修复。',
        kind: 'skill',
        target: 'troubleshoot',
      },
      {
        id: 'query-verified-cases',
        title: '再查验证过的案例',
        goal: '把候选根因收敛到已有的解决模式。',
        kind: 'skill',
        target: 'query_cases',
        args: { action: 'query' },
        dependsOn: ['semantic-diagnosis'],
      },
      {
        id: 'load-targeted-standards',
        title: '按根因补加载规范',
        goal: '只有定位到主题后再加载最小够用规范。',
        kind: 'skill',
        target: 'get_standards',
        args: { mode: 'key-rules' },
        dependsOn: ['query-verified-cases'],
      },
    ],
    completionCriteria: ['已经定位到根因或候选根因', '修复方案来自案例或规范而不是拍脑袋', '验证路径明确'],
    codegenPrerequisites: {
      completedStepIds: ['semantic-diagnosis'],
      note: '问题修复前至少要先完成一次语义诊断，避免直接拍脑袋改代码。',
    },
  },
  {
    id: 'standards_setup',
    title: '规范与配置工作流',
    subagentRole: 'Standards And Setup Planner',
    category: 'setup',
    description: '按 Codex 子代理的配置规划专员思路，先分析项目，再匹配 agent 和最小规范入口。',
    entrySignals: ['setup', '配置', '初始化', 'instructions', 'copilot-instructions', 'agent', '模板', '规范', 'bootstrap'],
    frameworks: ['flutter', 'vue3', 'react', 'wechat-miniprogram', 'generic'],
    requiredInputs: ['项目路径', '目标框架或技术栈', '是否生成配置文件'],
    relatedSkills: ['analyze_project', 'manage_agents', 'setup_project', 'get_standards'],
    relatedStandards: ['flutter', 'dart-base', 'typescript-base', 'vue3-composition'],
    steps: [
      {
        id: 'analyze-project',
        title: '先分析项目技术栈',
        goal: '避免在未知技术栈下硬塞错误 agent 或规范。',
        kind: 'skill',
        target: 'analyze_project',
        requiredInputs: ['projectPath'],
      },
      {
        id: 'match-agents',
        title: '匹配 agent 与入口',
        goal: '先缩小成适合当前项目的一组 agent / workflow。',
        kind: 'skill',
        target: 'manage_agents',
        args: { action: 'match' },
        dependsOn: ['analyze-project'],
      },
      {
        id: 'setup-project',
        title: '生成或更新项目配置',
        goal: '把选好的 agent 和规范入口真正写进项目。',
        kind: 'skill',
        target: 'setup_project',
        args: { action: 'auto_setup', generateInstructions: true },
        dependsOn: ['match-agents'],
        requiredInputs: ['projectPath'],
      },
      {
        id: 'load-minimal-standards',
        title: '最后补加载最小规范集',
        goal: '让后续实现阶段从 compact/精确标准入口开始。',
        kind: 'skill',
        target: 'get_standards',
        args: { mode: 'key-rules' },
        dependsOn: ['setup-project'],
      },
    ],
    completionCriteria: ['项目已识别技术栈', 'agent 路由已匹配', 'instructions/config 已生成或更新'],
    codegenPrerequisites: {
      completedStepIds: ['load-minimal-standards'],
      note: '进入实现前，至少要完成项目分析、配置写入和最小规范加载。',
    },
  },
  {
    id: 'customization_maintenance',
    title: 'Agent 定制与维护工作流',
    subagentRole: 'Customization Maintenance Specialist',
    category: 'setup',
    description: '按 Codex 子代理的 customization 维护专员思路处理 agent、prompt、skill、copilot-instructions 与路由命中问题，先诊断结构，再决定生成或更新配置。',
    entrySignals: ['agent', 'prompt', 'skill', 'copilot-instructions', 'customization', 'frontmatter', 'routing', 'discoverability', '命中', '不生效', '配置不对', 'instructions'],
    frameworks: ['generic'],
    requiredInputs: ['项目路径或 customization 文件路径', '当前问题或目标', '涉及的 customization 类型'],
    relatedSkills: ['health_check', 'analyze_project', 'get_template', 'setup_project', 'manage_agents'],
    relatedStandards: [],
    steps: [
      {
        id: 'inspect-customization-health',
        title: '先检查配置健康状态',
        goal: '先判断现有 MCP / instructions / agent 配置是否已经损坏或缺失。',
        kind: 'skill',
        target: 'health_check',
        requiredInputs: ['workspacePath'],
      },
      {
        id: 'inventory-customization-surface',
        title: '盘点项目与定制化面',
        goal: '确认项目结构、技术栈与 customization 落点，避免在错误目录上修。',
        kind: 'skill',
        target: 'analyze_project',
        dependsOn: ['inspect-customization-health'],
        requiredInputs: ['projectPath'],
      },
      {
        id: 'locate-reusable-templates',
        title: '定位可复用模板',
        goal: '先查看是否已有配置模板或 instructions 模板可复用，而不是手写一套平行结构。',
        kind: 'skill',
        target: 'get_template',
        args: { action: 'list', search: 'copilot' },
        dependsOn: ['inventory-customization-surface'],
      },
      {
        id: 'regenerate-customization-config',
        title: '生成或更新配置骨架',
        goal: '基于项目上下文更新 copilot-instructions 和相关配置入口。',
        kind: 'skill',
        target: 'setup_project',
        args: { action: 'generate', updateMode: 'merge' },
        dependsOn: ['inspect-customization-health', 'inventory-customization-surface'],
        requiredInputs: ['projectPath'],
      },
      {
        id: 'review-customization-routing',
        title: '检查路由与保留块',
        goal: '确认描述关键词、CUSTOM 区块与 discoverability 没有在更新中丢失。',
        kind: 'checkpoint',
        dependsOn: ['regenerate-customization-config'],
        note: '重点检查命中关键词、CUSTOM_START/CUSTOM_END 保护区、生成文件路径与路由描述。',
      },
    ],
    completionCriteria: ['定制化入口已明确', '配置更新路径清晰且保留用户自定义块', 'agent / prompt / instructions 路由没有丢 discoverability'],
    codegenPrerequisites: {
      completedStepIds: ['inspect-customization-health', 'inventory-customization-surface'],
      requiredInputs: ['projectPath'],
      note: '在生成或覆盖 customization 配置前，必须先完成健康检查与项目盘点，避免误写错误路径或覆盖有效定制。',
    },
  },
  {
    id: 'reference_clone',
    title: '参考项目克隆工作流',
    subagentRole: 'Reference Clone Planner',
    category: 'delivery',
    description: '按 Codex 子代理的克隆规划专员思路先吃透参考项目，再生成新项目骨架。',
    entrySignals: ['clone', '参考项目', '仿照', '脚手架', '新项目', '骨架', '迁移', '同风格'],
    frameworks: ['generic'],
    requiredInputs: ['参考项目路径', '新项目路径', '新项目名称'],
    relatedSkills: ['clone_analyze', 'clone_generate', 'get_template'],
    relatedStandards: ['component-design', 'api-layer'],
    steps: [
      {
        id: 'analyze-reference',
        title: '先分析参考项目',
        goal: '先拿到层次、约定和差异点，不直接复制。',
        kind: 'skill',
        target: 'clone_analyze',
        requiredInputs: ['referenceProjectPath'],
      },
      {
        id: 'generate-skeleton',
        title: '生成新项目骨架',
        goal: '把分析结果落成可执行的层级和待办清单。',
        kind: 'skill',
        target: 'clone_generate',
        dependsOn: ['analyze-reference'],
        requiredInputs: ['referenceProjectPath', 'newProjectPath', 'newProjectName'],
      },
      {
        id: 'supplement-templates',
        title: '按需补模板',
        goal: '仅在缺层时再补模板，而不是一开始就全量套模板。',
        kind: 'skill',
        target: 'get_template',
        args: { action: 'list' },
        dependsOn: ['generate-skeleton'],
      },
    ],
    completionCriteria: ['参考项目分析完成', '新骨架已生成', '模板补充点明确'],
    codegenPrerequisites: {
      completedStepIds: ['analyze-reference'],
      note: '开始生成新项目内容前，必须先完成参考项目分析。',
    },
  },
  {
    id: 'multi_step_reasoning',
    title: '复杂任务拆解工作流',
    subagentRole: 'Planning And Reasoning Specialist',
    category: 'planning',
    description: '按 Codex 子代理的规划专员思路，把模糊或大型任务先拆成步骤，再交给具体技能处理。',
    entrySignals: ['复杂', '拆解', '分步', '规划', 'plan', 'architecture', '大改', '迁移', '多步骤', '思路'],
    frameworks: ['generic'],
    requiredInputs: ['任务目标', '范围边界', '是否已有项目上下文'],
    relatedSkills: ['analyze_project', 'get_standards', 'troubleshoot'],
    relatedStandards: ['problem-diagnosis', 'design-restoration'],
    steps: [
      {
        id: 'use-sequential-thinking',
        title: '先做多步推理',
        goal: '先把任务拆成可执行阶段，避免一上来就混杂实现。',
        kind: 'tool',
        target: 'sequential_thinking',
      },
      {
        id: 'analyze-context',
        title: '补项目上下文',
        goal: '确认技术栈、边界和已有结构。',
        kind: 'skill',
        target: 'analyze_project',
        dependsOn: ['use-sequential-thinking'],
        requiredInputs: ['projectPath'],
      },
      {
        id: 'route-to-specialist',
        title: '再切到专门工作流',
        goal: '将拆解结果交给设计还原、排障、配置等具体流程。',
        kind: 'checkpoint',
        dependsOn: ['analyze-context'],
        note: '通常下一步会切换到 sketch_restoration、problem_diagnosis 或 standards_setup。',
      },
    ],
    completionCriteria: ['任务已被拆成阶段', '每个阶段都有后续 workflow 或 skill 入口'],
  },
];

function normalizeText(value: string | undefined): string {
  return (value || '').trim().toLowerCase();
}

function normalizeStringList(values?: string[]): string[] {
  if (!Array.isArray(values)) {
    return [];
  }

  return [...new Set(values.map((value) => String(value).trim()).filter(Boolean))];
}

function createSuggestedCalls(steps: WorkflowStep[]): WorkflowCallSuggestion[] {
  return steps
    .filter((step) => step.kind !== 'checkpoint')
    .slice(0, 3)
    .map((step) => ({
      kind: step.kind,
      target: step.target,
      args: step.args,
      why: step.goal,
    }));
}

function createCallSuggestion(step: WorkflowStep): WorkflowCallSuggestion {
  return {
    kind: step.kind,
    target: step.target,
    args: step.args,
    why: step.goal,
  };
}

function getWorkflow(workflowId: string): WorkflowDefinition | null {
  return WORKFLOW_DEFINITIONS.find((item) => item.id === workflowId) || null;
}

function getStepMap(workflow: WorkflowDefinition): Map<string, WorkflowStep> {
  return new Map(workflow.steps.map((step) => [step.id, step]));
}

function detectFramework(task: string | undefined, framework?: string, currentFile?: string): string {
  const explicit = normalizeText(framework);
  if (explicit) {
    return explicit;
  }

  const normalizedTask = normalizeText(task);
  const normalizedFile = normalizeText(currentFile);

  if (normalizedTask.includes('flutter') || normalizedTask.includes('dart') || normalizedFile.endsWith('.dart')) {
    return 'flutter';
  }
  if (normalizedTask.includes('vue') || normalizedFile.endsWith('.vue')) {
    return 'vue3';
  }
  if (normalizedTask.includes('react') || normalizedFile.endsWith('.tsx') || normalizedFile.endsWith('.jsx')) {
    return 'react';
  }
  return 'generic';
}

function scoreWorkflow(task: string, detectedFramework: string, workflow: WorkflowDefinition): { score: number; matchedSignals: string[] } {
  const normalizedTask = normalizeText(task);
  const matchedSignals: string[] = [];
  let score = 0;

  if (workflow.frameworks.includes(detectedFramework) || workflow.frameworks.includes('generic')) {
    score += workflow.frameworks.includes(detectedFramework) ? 5 : 1;
    if (workflow.frameworks.includes(detectedFramework) && detectedFramework !== 'generic') {
      matchedSignals.push(`framework:${detectedFramework}`);
    }
  }

  for (const signal of workflow.entrySignals) {
    const normalizedSignal = normalizeText(signal);
    if (normalizedSignal && normalizedTask.includes(normalizedSignal)) {
      matchedSignals.push(signal);
      score += normalizedSignal.length >= 4 ? 4 : 3;
    }
  }

  if (workflow.id === 'sketch_restoration' && /design|设计稿|sketch|还原|measure|测量/.test(normalizedTask)) {
    score += 6;
  }

  if (workflow.id === 'problem_diagnosis' && /bug|error|异常|问题|regression|回归|broken/.test(normalizedTask)) {
    score += 6;
  }

  if (workflow.id === 'standards_setup' && /setup|配置|instructions|agent|规范|bootstrap|初始化/.test(normalizedTask)) {
    score += 6;
  }

  if (workflow.id === 'customization_maintenance' && /agent|prompt|skill|copilot-instructions|customization|frontmatter|routing|discoverability|命中|不生效|配置/.test(normalizedTask)) {
    score += 7;
  }

  if (workflow.id === 'multi_step_reasoning' && /复杂|规划|分步|architecture|plan|迁移|大改/.test(normalizedTask)) {
    score += 5;
  }

  return {
    score,
    matchedSignals: [...new Set(matchedSignals)],
  };
}

function toConfidence(score: number): 'high' | 'medium' | 'low' {
  if (score >= 12) return 'high';
  if (score >= 7) return 'medium';
  return 'low';
}

function resolveMissingItems(step: WorkflowStep, completedSteps: Set<string>, loadedStandards: Set<string>, availableInputs: Set<string>): {
  missingSteps: string[];
  missingStandards: string[];
  missingInputs: string[];
} {
  return {
    missingSteps: (step.dependsOn || []).filter((dependency) => !completedSteps.has(dependency)),
    missingStandards: (step.requiredStandards || []).filter((standard) => !loadedStandards.has(standard)),
    missingInputs: (step.requiredInputs || []).filter((input) => !availableInputs.has(input)),
  };
}

function getFallbackCalls(workflow: WorkflowDefinition, step: WorkflowStep, completedSteps: Set<string>): WorkflowCallSuggestion[] {
  const stepMap = getStepMap(workflow);

  return (step.fallbackStepIds || [])
    .map((stepId) => stepMap.get(stepId))
    .filter((candidate): candidate is WorkflowStep => Boolean(candidate) && !completedSteps.has(candidate.id))
    .map((candidate) => createCallSuggestion(candidate));
}

function isCodegenOperation(operation: WorkflowOperation): boolean {
  return ['create_file', 'edit_file', 'multi_edit', 'generate_code'].includes(operation);
}

export function listWorkflows(filter?: { framework?: string; category?: WorkflowCategory }): {
  workflows: Array<{
    id: string;
    title: string;
    subagentRole: string;
    category: WorkflowCategory;
    description: string;
    frameworks: string[];
    relatedSkills: string[];
    stepsCount: number;
  }>;
} {
  const framework = normalizeText(filter?.framework);
  const category = filter?.category;

  const workflows = WORKFLOW_DEFINITIONS
    .filter((workflow) => !category || workflow.category === category)
    .filter((workflow) => !framework || workflow.frameworks.includes(framework) || workflow.frameworks.includes('generic'))
    .map((workflow) => ({
      id: workflow.id,
      title: workflow.title,
      subagentRole: workflow.subagentRole,
      category: workflow.category,
      description: workflow.description,
      frameworks: workflow.frameworks,
      relatedSkills: workflow.relatedSkills,
      stepsCount: workflow.steps.length,
    }));

  return { workflows };
}

export function getWorkflowDetail(workflowId: string): (WorkflowDefinition & { suggestedCalls: WorkflowCallSuggestion[] }) | null {
  const workflow = getWorkflow(workflowId);
  if (!workflow) {
    return null;
  }

  return {
    ...workflow,
    steps: workflow.steps.map((step) => ({ ...step })),
    suggestedCalls: createSuggestedCalls(workflow.steps),
  };
}

export function recommendWorkflow(args: {
  task: string;
  framework?: string;
  currentFile?: string;
  limit?: number;
}): {
  task: string;
  detectedFramework: string;
  recommendations: WorkflowRecommendation[];
  guidance: string;
} {
  const detectedFramework = detectFramework(args.task, args.framework, args.currentFile);
  const limit = typeof args.limit === 'number' && args.limit > 0 ? args.limit : 3;

  const recommendations = WORKFLOW_DEFINITIONS
    .map((workflow) => {
      const scored = scoreWorkflow(args.task, detectedFramework, workflow);
      return {
        workflow: {
          id: workflow.id,
          title: workflow.title,
          subagentRole: workflow.subagentRole,
          description: workflow.description,
          category: workflow.category,
        },
        score: scored.score,
        confidence: toConfidence(scored.score),
        matchedSignals: scored.matchedSignals,
        suggestedCalls: createSuggestedCalls(workflow.steps),
      } satisfies WorkflowRecommendation;
    })
    .filter((item) => item.score > 0)
    .sort((left, right) => right.score - left.score)
    .slice(0, limit);

  const fallback = recommendations.length > 0
    ? recommendations
    : WORKFLOW_DEFINITIONS
        .filter((workflow) => ['problem_diagnosis', 'standards_setup'].includes(workflow.id))
        .map((workflow) => ({
          workflow: {
            id: workflow.id,
            title: workflow.title,
            subagentRole: workflow.subagentRole,
            description: workflow.description,
            category: workflow.category,
          },
          score: 1,
          confidence: 'low' as const,
          matchedSignals: [],
          suggestedCalls: createSuggestedCalls(workflow.steps),
        }));

  return {
    task: args.task,
    detectedFramework,
    recommendations: fallback,
    guidance: '复杂任务建议先调用 recommend_workflow，选中最匹配的 workflow 后，再使用 advance_workflow 逐步推进，并在代码生成前调用 validate_workflow。',
  };
}

export function advanceWorkflow(args: WorkflowAdvanceArgs): WorkflowAdvanceResult | null {
  const workflow = getWorkflow(args.workflowId);
  if (!workflow) {
    return null;
  }

  const detectedFramework = detectFramework(args.task, args.framework, args.currentFile);
  const completedSteps = new Set(normalizeStringList(args.completedSteps));
  const blockedSteps = new Set(normalizeStringList(args.blockedSteps));
  const availableInputs = new Set(normalizeStringList(args.availableInputs));
  const loadedStandards = new Set(normalizeStringList(args.loadedStandards));
  const pendingSteps = workflow.steps.filter((step) => !completedSteps.has(step.id));

  if (pendingSteps.length === 0) {
    return {
      workflow: {
        id: workflow.id,
        title: workflow.title,
        subagentRole: workflow.subagentRole,
        description: workflow.description,
      },
      detectedFramework,
      status: 'completed',
      progress: {
        completedCount: workflow.steps.length,
        totalCount: workflow.steps.length,
        completedSteps: [...completedSteps],
        remainingSteps: [],
      },
      guidance: `workflow ${workflow.id} 已完成。下一步应按完成标准做自检：${workflow.completionCriteria.join('；')}`,
    };
  }

  const nextStep = pendingSteps[0];
  const missing = resolveMissingItems(nextStep, completedSteps, loadedStandards, availableInputs);
  const isMarkedBlocked = blockedSteps.has(nextStep.id);

  if (missing.missingSteps.length > 0 || missing.missingInputs.length > 0 || missing.missingStandards.length > 0 || isMarkedBlocked) {
    return {
      workflow: {
        id: workflow.id,
        title: workflow.title,
        subagentRole: workflow.subagentRole,
        description: workflow.description,
      },
      detectedFramework,
      status: 'blocked',
      progress: {
        completedCount: completedSteps.size,
        totalCount: workflow.steps.length,
        completedSteps: [...completedSteps],
        remainingSteps: pendingSteps.map((step) => step.id),
      },
      nextStep: { ...nextStep },
      nextCall: nextStep.kind === 'checkpoint' ? undefined : createCallSuggestion(nextStep),
      blockedBy: {
        stepId: nextStep.id,
        missingSteps: missing.missingSteps,
        missingInputs: missing.missingInputs,
        missingStandards: missing.missingStandards,
      },
      fallbackCalls: getFallbackCalls(workflow, nextStep, completedSteps),
      guidance: isMarkedBlocked
        ? `当前 workflow 被显式标记为阻塞在 ${nextStep.id}，请先处理 fallback 或补齐上下文。`
        : `当前 workflow 卡在 ${nextStep.id}。先补齐缺失前置条件，再继续推进。`,
    };
  }

  if (nextStep.kind === 'checkpoint') {
    return {
      workflow: {
        id: workflow.id,
        title: workflow.title,
        subagentRole: workflow.subagentRole,
        description: workflow.description,
      },
      detectedFramework,
      status: 'checkpoint',
      progress: {
        completedCount: completedSteps.size,
        totalCount: workflow.steps.length,
        completedSteps: [...completedSteps],
        remainingSteps: pendingSteps.map((step) => step.id),
      },
      nextStep: { ...nextStep },
      guidance: nextStep.note || nextStep.goal,
    };
  }

  return {
    workflow: {
      id: workflow.id,
      title: workflow.title,
      subagentRole: workflow.subagentRole,
      description: workflow.description,
    },
    detectedFramework,
    status: 'ready',
    progress: {
      completedCount: completedSteps.size,
      totalCount: workflow.steps.length,
      completedSteps: [...completedSteps],
      remainingSteps: pendingSteps.map((step) => step.id),
    },
    nextStep: { ...nextStep },
    nextCall: createCallSuggestion(nextStep),
    fallbackCalls: getFallbackCalls(workflow, nextStep, completedSteps),
    guidance: `下一步应执行 ${nextStep.id}。完成后将该 step 加入 completedSteps，再次调用 advance_workflow 获取后续动作。`,
  };
}

export function validateWorkflow(args: WorkflowValidationArgs): WorkflowValidationResult | null {
  const workflow = getWorkflow(args.workflowId);
  if (!workflow) {
    return null;
  }

  const detectedFramework = detectFramework(args.task, args.framework, args.currentFile);
  const completedSteps = new Set(normalizeStringList(args.completedSteps));
  const availableInputs = new Set(normalizeStringList(args.availableInputs));
  const loadedStandards = new Set(normalizeStringList(args.loadedStandards));
  const suggestedCalls: WorkflowCallSuggestion[] = [];
  let missingSteps: string[] = [];
  let missingInputs: string[] = [];
  let missingStandards: string[] = [];
  let reason = '✅ workflow 校验通过';

  if (isCodegenOperation(args.operation) && workflow.codegenPrerequisites) {
    missingSteps = workflow.codegenPrerequisites.completedStepIds.filter((stepId) => !completedSteps.has(stepId));
    missingInputs = (workflow.codegenPrerequisites.requiredInputs || []).filter((input) => !availableInputs.has(input));
    missingStandards = (workflow.codegenPrerequisites.requiredStandards || []).filter((standard) => !loadedStandards.has(standard));

    if (missingStandards.length > 0) {
      suggestedCalls.push({
        kind: 'skill',
        target: 'get_standards',
        args: { ids: missingStandards, mode: 'key-rules' },
        why: '先补齐代码生成前必须加载的规范。',
      });
    }

    const stepMap = getStepMap(workflow);
    for (const stepId of missingSteps) {
      const step = stepMap.get(stepId);
      if (step && step.kind !== 'checkpoint') {
        suggestedCalls.push(createCallSuggestion(step));
      }
    }

    if (missingSteps.length > 0 || missingInputs.length > 0 || missingStandards.length > 0) {
      reason = workflow.codegenPrerequisites.note;
    }
  } else {
    const progress = advanceWorkflow({
      workflowId: args.workflowId,
      task: args.task,
      framework: args.framework,
      currentFile: args.currentFile,
      completedSteps: [...completedSteps],
      availableInputs: [...availableInputs],
      loadedStandards: [...loadedStandards],
    });

    if (progress?.status === 'blocked' && progress.blockedBy) {
      missingSteps = progress.blockedBy.missingSteps;
      missingInputs = progress.blockedBy.missingInputs;
      missingStandards = progress.blockedBy.missingStandards;
      reason = progress.guidance;

      if (progress.nextCall) {
        suggestedCalls.push(progress.nextCall);
      }
      if (progress.fallbackCalls) {
        suggestedCalls.push(...progress.fallbackCalls);
      }
    } else if (progress?.status === 'checkpoint') {
      reason = progress.guidance;
      missingSteps = progress.nextStep ? [progress.nextStep.id] : [];
    }
  }

  return {
    workflow: {
      id: workflow.id,
      title: workflow.title,
      operation: args.operation,
    },
    canProceed: missingSteps.length === 0 && missingInputs.length === 0 && missingStandards.length === 0,
    detectedFramework,
    missingSteps,
    missingInputs,
    missingStandards,
    suggestedCalls,
    reason,
  };
}